import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getThreadAccess,
  updateThreadAccess,
  revokeThreadAccess,
  ThreadAccessResponse,
  RevokeAccessResponse,
  PublicShareLink,
  SharedRecipient,
} from "src/services/threads";
import { toast } from "react-toastify";

// ── Types ──────────────────────────────────────────────────────────────────

interface SharedAccessData {
  sharedThreadId: string;
  sharedThreadTitle: string;
  sharedType: "public" | "named" | "private" | null;
  publicShareLink: PublicShareLink | null;
  sharedRecipients: SharedRecipient[];
}

interface ShareState {
  showShareModal: boolean;
  sharedAccessData: SharedAccessData | null;
  fetchStatus: "idle" | "loading" | "success" | "failed";
  fetchStatusError: unknown;
  actionStatus: "idle" | "loading";
  actionError: unknown;
}

// ── Thunks ──────────────────────────────────────────────────────────────────

export const fetchThreadAccess = createAsyncThunk<ThreadAccessResponse, string>(
  "share/fetchThreadAccess",
  async (sharedThreadId, thunkAPI) => {
    try {
      return await getThreadAccess(sharedThreadId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || { error: "Failed to fetch access" });
    }
  }
);

export const switchToPublic = createAsyncThunk<ThreadAccessResponse, string>(
  "share/switchToPublic",
  async (sharedThreadId, thunkAPI) => {
    try {
      return await updateThreadAccess(sharedThreadId, { shared_access_type: "public" });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || { error: "Failed to switch to public" });
    }
  }
);

export const makePrivate = createAsyncThunk<ThreadAccessResponse, string>(
  "share/makePrivate",
  async (sharedThreadId, thunkAPI) => {
    try {
      return await updateThreadAccess(sharedThreadId, { shared_access_type: "private" });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || { error: "Failed to make private" });
    }
  }
);

export const addRecipient = createAsyncThunk<ThreadAccessResponse, { threadId: string; email: string }>(
  "share/addRecipient",
  async ({ threadId, email }, thunkAPI) => {
    try {
      return await updateThreadAccess(threadId, { shared_access_type: "named", email });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || { error: "Failed to add recipient" });
    }
  }
);

export const revokeRecipient = createAsyncThunk<RevokeAccessResponse, { threadId: string; email: string; revokeAllThreads?: boolean }>(
  "share/revokeRecipient",
  async ({ threadId, email, revokeAllThreads = false }, thunkAPI) => {
    try {
      return await revokeThreadAccess(threadId, { email, revoke_all_threads: revokeAllThreads });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data || { error: "Failed to revoke" });
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────────────

const initialState: ShareState = {
  showShareModal: false,
  sharedAccessData: null,
  fetchStatus: "idle",
  fetchStatusError: null,
  actionStatus: "idle",
  actionError: null,
};

const shareSlice = createSlice({
  name: "share",
  initialState,
  reducers: {
    openShareModal: (state, action: PayloadAction<{ sharedThreadId: string; sharedThreadTitle: string }>) => {
      state.showShareModal = true;
      state.sharedAccessData = {
        sharedThreadId: action.payload.sharedThreadId,
        sharedThreadTitle: action.payload.sharedThreadTitle,
        sharedType: null,
        publicShareLink: null,
        sharedRecipients: [],
      };
      state.fetchStatus = "idle";
      state.fetchStatusError = null;
      state.actionStatus = "idle";
      state.actionError = null;
    },
    closeShareModal: (state) => {
      state.showShareModal = false;
      state.sharedAccessData = null;
      state.fetchStatus = "idle";
      state.fetchStatusError = null;
      state.actionStatus = "idle";
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    const applyAccessResponse = (state: ShareState, payload: ThreadAccessResponse) => {
      if (state.sharedAccessData) {
        state.sharedAccessData.sharedType = payload.shared_access_type;
        state.sharedAccessData.publicShareLink = payload.public_share_link;
        state.sharedAccessData.sharedRecipients = payload.shared_recipients;
      }
    };

    builder
      // Fetch access
      .addCase(fetchThreadAccess.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchStatusError = null;
      })
      .addCase(fetchThreadAccess.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        applyAccessResponse(state, action.payload);
      })
      .addCase(fetchThreadAccess.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchStatusError = action.payload;
      })
      // Switch to public
      .addCase(switchToPublic.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(switchToPublic.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        applyAccessResponse(state, action.payload);
        toast.success("Public link created!");
      })
      .addCase(switchToPublic.rejected, (state, action) => {
        state.actionStatus = "idle";
        state.actionError = action.payload;
        const msg = (action.payload as any)?.detail || "Failed to create public link";
        toast.error(typeof msg === "string" ? msg : "Failed to create public link");
      })
      // Add recipient
      .addCase(addRecipient.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(addRecipient.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        applyAccessResponse(state, action.payload);
        toast.success("Recipient added!");
      })
      .addCase(addRecipient.rejected, (state, action) => {
        state.actionStatus = "idle";
        const msg = (action.payload as any)?.detail || "Failed to add recipient";
        toast.error(typeof msg === "string" ? msg : "Failed to add recipient");
      })
      // Revoke recipient
      .addCase(revokeRecipient.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(revokeRecipient.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        if (state.sharedAccessData) {
          state.sharedAccessData.sharedRecipients = state.sharedAccessData.sharedRecipients.filter(
            (r: SharedRecipient) => r.email !== action.payload.revoked_recipient.email
          );
          state.sharedAccessData.sharedType = action.payload.shared_access_type;
        }
        toast.success(`Access revoked for ${action.payload.revoked_recipient.email}`);
      })
      .addCase(revokeRecipient.rejected, (state) => {
        state.actionStatus = "idle";
        toast.error("Failed to revoke access");
      })
      // Make private
      .addCase(makePrivate.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(makePrivate.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        applyAccessResponse(state, action.payload);
        toast.success("Sharing stopped. Conversation is now private.");
      })
      .addCase(makePrivate.rejected, (state) => {
        state.actionStatus = "idle";
        toast.error("Failed to make private");
      });
  },
});

export const { openShareModal, closeShareModal } = shareSlice.actions;
export default shareSlice.reducer;
